import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  buyCredits,
  cancelSubscription,
  createProject,
  deleteProject,
  getAccount,
  getMe,
  getUsage,
  listCreditPacks,
  listOutcomes,
  listProjects,
  subscribe,
  type Account,
  type CreditPack,
  type Project,
  type UsageReport,
  type User,
} from '@/api';
import { clearToken, getToken, getUser, isImpersonating, isLoggedIn, setToken, stopImpersonation } from '@/auth/token';
import Sidebar, { type ViewKey } from './components/Sidebar';
import TopBar from './components/TopBar';
import OverviewView from './components/OverviewView';
import ProjectsView from './components/ProjectsView';
import CreditsView from './components/CreditsView';
import PlaybooksView from './components/PlaybooksView';
import { seedStudioChatTab, startNewPlaybook } from '@/pages/home/studioEntry';
import TokenUsageView from './components/TokenUsageView';
import { outcomeScores } from './outcomeScore';
import { usageFromTransactions } from './usage';

const VIEWS: ViewKey[] = ['overview', 'playbooks', 'projects', 'credits', 'usage'];

function isView(v: string | null): v is ViewKey {
  return !!v && (VIEWS as string[]).includes(v);
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const view: ViewKey = isView(params.get('view')) ? params.get('view') as ViewKey : 'overview';

  const [acc, setAcc] = useState<Account | null>(null);
  const [tokenUsage, setTokenUsage] = useState<UsageReport | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [scores, setScores] = useState<Record<string, number>>({});
  const [packs, setPacks] = useState<CreditPack[]>([]);
  const [me, setMe] = useState<User | null>(getUser());
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [createErr, setCreateErr] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const setView = (v: ViewKey) => {
    const next = new URLSearchParams(params);
    next.set('view', v);
    next.delete('new');
    setParams(next, { replace: true });
  };

  useEffect(() => {
    document.title = 'Dashboard — Sourcetape';
  }, []);

  useEffect(() => {
    if (!isLoggedIn()) {
      const next = `/dashboard${params.toString() ? `?${params.toString()}` : ''}`;
      navigate(`/login?next=${encodeURIComponent(next)}`);
      return;
    }
    getAccount().then(setAcc).catch((e) => setErr(e instanceof Error ? e.message : 'failed'));
    getUsage().then(setTokenUsage).catch(() => { /* ignore */ });
    listProjects().then(setProjects).catch(() => { /* ignore */ });
    listOutcomes()
      .then((os) => setScores(outcomeScores(os)))
      .catch(() => { /* ignore */ });
    listCreditPacks()
      .then((r) => setPacks(r.packs || []))
      .catch(() => { /* ignore */ });
    getMe()
      .then((u) => {
        setMe(u);
        const tok = getToken();
        if (tok) setToken(tok, u);
      })
      .catch(() => { /* ignore */ });
    if (params.get('checkout') === 'success') setNote('Payment received. Hosting and credits update after Stripe confirms.');
    if (params.get('checkout') === 'cancel') setNote('Checkout cancelled. No credits were added.');
  }, [navigate, params]);

  const act = async (fn: () => Promise<Account>) => {
    setErr(null);
    setBusy(true);
    try {
      const next = await fn();
      if (next.url) {
        window.location.assign(next.url);
        return;
      }
      setAcc(next);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'failed');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!acc) return;
    const pack = params.get('pack');
    if (!pack) return;
    const lock = `sh_autopack:${pack}`;
    try {
      if (sessionStorage.getItem(lock)) return;
      sessionStorage.setItem(lock, '1');
    } catch {
      /* ignore */
    }
    setView('credits');
    act(() => buyCredits(pack));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acc, params]);

  const usage = useMemo(() => usageFromTransactions(acc?.transactions || []), [acc]);

  const handleNewPlaybook = async () => {
    setCreateErr(null);
    setCreating(true);
    const result = await startNewPlaybook({
      create: (p) => createProject(p),
      seedTab: seedStudioChatTab,
      navigate,
    });
    if (!result.ok) setCreateErr(result.error || 'failed');
    setCreating(false);
  };

  useEffect(() => {
    if (params.get('new') !== '1') return;
    const next = new URLSearchParams(params);
    next.delete('new');
    if (!next.get('view')) next.set('view', 'projects');
    setParams(next, { replace: true });
  }, [params, setParams]);

  const remove = async (id: string) => {
    if (!confirm('Delete this project?')) return;
    try {
      await deleteProject(id);
      setProjects((ps) => ps.filter((p) => p.id !== id));
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'failed');
    }
  };

  const displayName = me?.name || me?.email || getUser()?.name || getUser()?.email || 'Account';
  const isAdmin = me?.role === 'admin' || getUser()?.role === 'admin';

  return (
    <div className="min-h-screen bg-bg text-textp" data-testid="dashboard">
      <TopBar
        credits={acc?.credit_balance ?? 0}
        name={displayName}
        isAdmin={isAdmin}
        onAddCredits={() => setView('credits')}
        onSignOut={() => {
          clearToken();
          navigate('/');
        }}
        impersonating={isImpersonating()}
        onStopImpersonating={() => {
          stopImpersonation();
          navigate('/admin');
        }}
      />

      <div className="flex flex-col lg:flex-row">
        <Sidebar view={view} onChange={setView} isAdmin={isAdmin} />

        <main className="flex-1 min-w-0 p-4 md:p-6 lg:p-8">
          {err && view !== 'credits' && (
            <div className="mb-4 text-sm text-error bg-error/10 border border-error/30 rounded-md px-3 py-2">{err}</div>
          )}
          {!acc && <div className="text-texts text-sm">Loading…</div>}
          {acc && view === 'overview' && (
            <OverviewView
              credits={acc.credit_balance}
              thisMonth={usage.thisMonth}
              hosted={usage.hosted}
              months={usage.months}
              breakdown={usage.breakdown}
              projects={projects}
              scores={scores}
              onNavigate={setView}
            />
          )}
          {acc && view === 'playbooks' && <PlaybooksView />}
          {acc && view === 'projects' && (
            <ProjectsView
              projects={projects}
              scores={scores}
              onNewPlaybook={handleNewPlaybook}
              onDelete={remove}
              creating={creating}
              createErr={createErr}
            />
          )}
          {acc && view === 'credits' && (
            <CreditsView
              acc={acc}
              packs={packs}
              busy={busy}
              err={err}
              note={note}
              onBuy={(pack) => act(() => buyCredits(pack))}
              onSubscribe={(plan) => act(() => subscribe(plan))}
              onCancel={() => act(() => cancelSubscription())}
            />
          )}
          {acc && view === 'usage' && (
            <TokenUsageView
              report={
                tokenUsage || {
                  total_tokens: 0,
                  prompt_tokens: 0,
                  completion_tokens: 0,
                  this_month_tokens: 0,
                  months: usage.months.map((m) => ({
                    month: m.month,
                    key: m.key,
                    tokens: 0,
                    prompt_tokens: 0,
                    completion_tokens: 0,
                  })),
                  by_model: [],
                  by_project: [],
                  by_kind: [],
                }
              }
            />
          )}
        </main>
      </div>
    </div>
  );
}
